import { DoCheck, Directive } from '@angular/core';
import { Braintree } from '@df/module-braintree/braintree.namespace';
import { AbstractBraintreeCardComponent } from '@df/module-braintree/hosted-fields/abstract-braintree-card.component';

@Directive()
export abstract class BraintreeHostedFieldsComponentDef extends AbstractBraintreeCardComponent implements DoCheck {
    /**
     * Save card model
     * @type {boolean}
     */
    _saveCard = false;

    /**
     * Selected card type model
     * @type {string | undefined}
     */
    selectedCardType: string | undefined;

    //////////////////////////////////////////
    /////
    /////   Payment method configuration
    /////
    //////////////////////////////////////////

    /**
     * Save card setter
     * @param {boolean} - value;
     */
    set saveCard(value: boolean) {
        const was = this._saveCard;
        this._saveCard = value;
        if (was !== value) {
            this.detectChanges();
        }
    }

    /**
     * Save card getter
     * @returns {boolean}
     */
    get saveCard(): boolean {
        return this.useSaveCard() && this._saveCard;
    }

    /**
     * If we should display save card checkbox in component
     * @returns {boolean}
     */
    useSaveCard(): boolean {
        return this.useVault() && this.app.customer.model.isLoggedIn;
    }

    /**
     * Returns if we should display cvv field
     * @returns {boolean}
     */
    hasVerification(): boolean {
        return this.method?.config.useCvv || false;
    }

    /**
     * If saving cards is enabled
     * @returns {boolean}
     */
    protected useVault(): boolean {
        return !!(this.method && this.method.config.vault);
    }

    /**
     * If hosted fields component is required
     * @returns {boolean}
     */
    protected areHostedFieldsNeeded(): boolean {
        return true;
    }

    //////////////////////////////////////////
    /////
    /////   Hosted fields methods
    /////
    //////////////////////////////////////////

    /**
     * Returns hosted-fields options for hosted-fields instance
     * @returns {Braintree.IHostedFieldsFieldOptions}
     */
    protected getHostedFieldsOptions(): Braintree.IHostedFieldsFieldOptions {
        const now = new Date();
        const month = now.getMonth() + 1;
        const fields: Braintree.IHostedFieldsFieldOptions = {
            number: {
                selector: `#card-number-${this.componentId}`,
                placeholder: '4111 1111 1111 1111'
            }
        };
        if (this.hasVerification()) {
            fields.cvv = {
                selector: `#cvv-${this.componentId}`,
                placeholder: '123'
            };
        }
        fields.expirationDate = {
            selector: `#expiration-date-${this.componentId}`,
            placeholder: `${month > 9 ? '' : 0}${month}/${now.getFullYear()}`
        };

        return fields;
    }

    /**
     * On hosted fields instance created callback
     * @param {Braintree.IHostedFieldsInstance} hostedFieldsInstance
     */
    protected override onHostedFieldsInstanceCreated(hostedFieldsInstance: Braintree.IHostedFieldsInstance): void {
        hostedFieldsInstance.on(Braintree.HostedFieldEventType.CardTypeChange, state => this.onCardTypeChange(state));
        super.onHostedFieldsInstanceCreated(hostedFieldsInstance);
    }

    /**
     * On card type change event callback
     * @param {Braintree.IHostedFieldsInstanceState} event
     */
    protected onCardTypeChange(hostedFieldsInstanceState: Braintree.IHostedFieldsInstanceState): void {
        if (hostedFieldsInstanceState.cards.length === 1) {
            this.selectedCardType = hostedFieldsInstanceState.cards[0].type;
        } else {
            this.selectedCardType = undefined;
        }
    }

    /**
     * Returns tokenize options
     * @returns {Braintree.ITokenizeOptions}
     */
    protected getTokenizeOptions(): Braintree.ITokenizeOptions {
        return {
            vault: this.saveCard
        };
    }

    /**
     * Returns tokenize payload
     * @param {Braintree.ITokenizeOptions} options
     * @returns {Promise<Braintree.ITokenizePayloadLike>}
     */
    protected getTokenizePayloadLike(options: Braintree.ITokenizeOptions): Promise<Braintree.IHostedFieldsTokenizePayloadLike> {
        return new Promise((resolve, reject) => {
            if (this.hostedFieldsInstance) {
                if (this.validateHostedFields()) {
                    this.hostedFieldsInstance
                        .tokenize(options)
                        .then(resolve)
                        .catch((error: Braintree.IError) => {
                            this.validateHostedFields();
                            reject(error && this.braintreeService.getTokenizeHostedFieldsErrorMessage(error));
                        });
                } else {
                    reject();
                }
            } else {
                reject(this.getScriptError());
            }
        });
    }

    //////////////////////////////////////////
    /////
    /////   Checkout
    /////
    //////////////////////////////////////////

    /**
     * Returns submission payment data
     * @param {string} nonce
     * @param {boolean} saveCard
     * @returns {Checkout.ISubmissionPaymentData}
     */
    protected getSubmissionPaymentData(nonce: string, saveCard = false): Braintree.IHostedFieldsPaymentSubmissionData {
        const data = {
            method: this.methodCode,
            additional_data: {
                payment_method_nonce: nonce
            }
        } as Braintree.IHostedFieldsPaymentSubmissionData;
        if (saveCard) {
            data.additional_data.store_cc = true;
        }
        return data;
    }
}
